import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { copyText } from "../../lib/browser";

interface Props {
  text: string;
  label: string;
  testId: string;
  compact?: boolean;
  copiedLabel?: string;
  tone?: "dark" | "light";
}

const TONES = {
  dark: { idle: "border-slate-700 bg-slate-800 text-slate-100 hover:border-slate-600 hover:bg-slate-700", copied: "border-emerald-500 bg-emerald-500/10 text-emerald-400" },
  light: { idle: "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-100", copied: "border-emerald-600 bg-emerald-50 text-emerald-700" },
};

export function CopyButton({ text, label, testId, compact = false, copiedLabel = "Copiado!", tone = "dark" }: Props) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const onCopy = async () => {
    if (!(await copyText(text))) return;
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  };

  const Icon = copied ? Check : Copy;
  const size = compact ? "min-h-[36px] px-3 py-1.5 text-xs" : "min-h-[44px] px-5 py-2.5 text-sm";
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      data-copied={copied}
      className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-md border font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${size} ${copied ? TONES[tone].copied : TONES[tone].idle}`}
    >
      <Icon aria-hidden="true" className="h-4 w-4" />
      {copied ? copiedLabel : label}
    </button>
  );
}
